import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    input,
    OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CurrentUserService } from '@core/services';
import { AppNavigationCreator } from '@layout/core/classes';
import { AppNavigationOptionsEnum } from '@layout/core/enums/my-orders-navigation-options.enum';
import { AppNavigation } from '@layout/core/interfaces';
import { LayoutService } from '@layout/core/services';
import { TranslateModule } from '@ngx-translate/core';
import { DecoIconComponent } from '@shared/components/deco-icon/deco-icon.component';
import { AppRoutes, OmRolesEnum } from '@shared/enums';
import { GeneralHelper } from '@shared/helpers';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-navigation',
    templateUrl: './app-navigation.component.html',
    styleUrls: ['./app-navigation.component.scss'],
    imports: [
        TranslateModule,
        PopoverModule,
        NgClass,
        RouterLink,
        RouterLinkActive,
        NgTemplateOutlet,
        TooltipModule,
        DecoIconComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppNavigationComponent implements OnInit {
    isSidebarCollapsed = input.required<boolean>();

    toggleIsCollapsed = output<void>();

    ovMyOrders = viewChild<Popover | null>('ovMyOrders');
    ovMySupplies = viewChild<Popover | null>('ovMySupplies');
    ovMyStrains = viewChild<Popover | null>('ovMyStrains');

    ovMyOrdersOpened = signal<boolean>(false);
    ovMySuppliesOpened = signal<boolean>(false);
    ovMyStrainsOpened = signal<boolean>(false);

    appNavigation = signal<AppNavigation[][]>([]);

    overlayStyleMyOrders = { 'margin-left': '93px', 'margin-top': '-175px' };
    overlayStyleMySupplies = { 'margin-left': '93px', 'margin-top': '-105px' };
    overlayStyleMyStrains = { 'margin-left': '93px', 'margin-top': '-140px' };

    myOrdersSubmenu: AppNavigation[] = AppNavigationCreator.createMyOrdersNavigation();
    mySuppliesSubmenu: AppNavigation[] = AppNavigationCreator.createMySuppliesNavigation();
    myStrainsSubmenu: AppNavigation[] = AppNavigationCreator.createMyStrainsNavigation();

    constructor(
        private router: Router,
        private currentUserService: CurrentUserService,
        private layoutService: LayoutService,
        private cdr: ChangeDetectorRef
    ) {
        this.layoutService.isSidebarCollapsed$.subscribe((isCollapsed: boolean) => {
            if (isCollapsed) {
                this.appNavigation().forEach(navList => {
                    navList.forEach(navItem => {
                        navItem.expanded = false;
                    });
                });
            }
        });

        this.router.events.subscribe(ev => {
            if (ev instanceof NavigationEnd) {
                this.cdr.detectChanges();
                this.handleStartOrderDisabledState();
            }
        });

        this.currentUserService.currentUser$.subscribe(() => {
            this.initAppNavigation();
        });
    }

    ngOnInit(): void {
        this.initAppNavigation();
    }

    navItemClick(navItem: AppNavigation): void {
        if (!navItem.submenu) {
            return;
        }

        navItem.expanded = !navItem.expanded;

        this.hideOverlays();

        if (navItem.expanded) {
            this.appNavigation().forEach(navList => {
                navList.forEach(item => {
                    if (item.expanded && item.id !== navItem.id) {
                        item.expanded = false;
                    }
                });
            });
        }

        if (this.isSidebarCollapsed()) {
            this.toggleIsCollapsed.emit();
        }
    }

    navItemHover(navItemId: string, event: any): void {
        if (!this.isSidebarCollapsed()) {
            return;
        }

        const itemTop = event.target.getBoundingClientRect().top + event.target.getBoundingClientRect().height / 2;

        if (navItemId === 'my-orders') {
            this.ovMySupplies()?.hide();
            this.ovMyStrains()?.hide();
            this.ovMyOrders()?.show(event);
            this.ovMyOrdersOpened.set(true);
            this.ovMySuppliesOpened.set(false);
            this.ovMyStrainsOpened.set(false);
            this.calculateOverlayPosition('my-orders', itemTop);
        } else if (navItemId === 'my-supplies') {
            this.ovMyOrders()?.hide();
            this.ovMyStrains()?.hide();
            this.ovMySupplies()?.show(event);
            this.ovMySuppliesOpened.set(true);
            this.ovMyOrdersOpened.set(false);
            this.ovMyStrainsOpened.set(false);
            this.calculateOverlayPosition('my-supplies', itemTop);
        } else if (navItemId === 'my-strains') {
            this.ovMyOrders()?.hide();
            this.ovMySupplies()?.hide();
            this.ovMyStrains()?.show(event);
            this.ovMyStrainsOpened.set(true);
            this.ovMySuppliesOpened.set(false);
            this.ovMyOrdersOpened.set(false);
            this.calculateOverlayPosition('my-strains', itemTop);
        } else {
            this.hideOverlays();
        }
    }

    private calculateOverlayPosition(navItemId: string, itemTop: number): void {
        setTimeout(() => {
            let overlayPanel: Popover | null = null;

            switch (navItemId) {
                case 'my-orders':
                    overlayPanel = this.ovMyOrders() || null;
                    break;
                case 'my-supplies':
                    overlayPanel = this.ovMySupplies() || null;
                    break;
                case 'my-strains':
                    overlayPanel = this.ovMyStrains() || null;
                    break;
            }

            if (overlayPanel?.container) {
                overlayPanel.container.style.top = itemTop + 'px';
                overlayPanel.container.style.marginTop = -overlayPanel.container.clientHeight / 2 + 'px';
                overlayPanel.container.style.position = 'fixed';
            }
        });
    }

    hideOverlays(): void {
        this.ovMyOrders()?.hide();
        this.ovMyOrdersOpened.set(false);

        this.ovMySupplies()?.hide();
        this.ovMySuppliesOpened.set(false);

        this.ovMyStrains()?.hide();
        this.ovMyStrainsOpened.set(false);
    }

    isRichMenuActive(navItem: AppNavigation): boolean {
        if (this.router.url === '/') {
            return false;
        }

        if (navItem.id === 'my-strains') {
            if (this.router.url.indexOf(AppRoutes.STRAIN_DETAILS) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.STRAINS_LISTING) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.STRAINS_SIF) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.STRAINS_DUPLICATE) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.STRAINS_CROSSING) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.STRAINS_CREATE_FROM_PROBES) > -1) {
                return true;
            }
        }

        if (navItem.id === 'my-orders') {
            if (this.router.url.indexOf(AppRoutes.ORDERS_LISTING) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.CKM_LISTING) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.CKM_DETAILS) > -1) {
                return true;
            }
            if (this.router.url.indexOf(AppRoutes.CKM_SETUP) > -1) {
                return true;
            }
        }

        return (navItem.submenu || []).findIndex(el => el.url.indexOf(this.router.url) > -1) > -1 || false;
    }

    initAppNavigation(): void {
        this.appNavigation.set([]);
        this.appNavigation().push(this.initNav());
    }

    initNav(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'dashboard',
                label: 'toolbar.dashboard',
                url: AppRoutes.DASHBOARD,
                icon: 'fa-light fa-objects-column',
                internalRoute: true,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'my-orders',
                label: 'toolbar.myOrders',
                url: AppRoutes.ORDERS_LISTING,
                icon: 'fa-light fa-basket-shopping',
                expanded: false,
                submenu: this.myOrdersSubmenu,
                internalRoute: true,
                isHidden: false,
            }),
            AppNavigationCreator.create({
                id: 'central-shipping',
                label: 'toolbar.centralShipping',
                url: AppRoutes.CENTRAL_SHIPPING,
                icon: 'fa-light fa-truck',
                internalRoute: true,
                isHidden: !this.currentUserService.hasRole(OmRolesEnum.CENTRAL_SHIPPING_ADMINISTRATOR),
            }),
            AppNavigationCreator.create({
                id: 'my-supplies',
                label: 'toolbar.mySupplies',
                url: AppRoutes.SUPPLIES,
                icon: 'fa-light fa-box-open-full',
                expanded: false,
                submenu: this.mySuppliesSubmenu,
                internalRoute: true,
                isHidden:
                    this.currentUserService.hasRole(OmRolesEnum.CENTRAL_SHIPPING_USER) &&
                    !this.currentUserService.hasRole(OmRolesEnum.CENTRAL_SHIPPING_ADMINISTRATOR),
            }),
            AppNavigationCreator.create({
                id: 'my-strains',
                label: 'toolbar.myStrains',
                url: AppRoutes.STRAINS_LISTING,
                icon: 'fa-light fa-dna',
                submenu: this.myStrainsSubmenu,
                internalRoute: true,
                isHidden: false,
            }),
        ];
    }

    handleStartOrderDisabledState() {
        const isOrderSetupActive = this.checkIfOrderSetupIsActive();
        const startNewOrderOption = this.myOrdersSubmenu.find(
            option => option.id === AppNavigationOptionsEnum.MY_ORDERS_START_NEW_ORDER
        );

        if (!startNewOrderOption) return;

        isOrderSetupActive ? (startNewOrderOption.disabled = true) : (startNewOrderOption.disabled = false);
    }

    private checkIfOrderSetupIsActive(): boolean {
        const urlWithoutQueryParams = GeneralHelper.getUrlWithoutQueryParams(this.router.url.toLowerCase());

        if (AppRoutes.ORDER_SETUP_STRAIN_SELECTION.valueOf().indexOf(urlWithoutQueryParams) > -1) return true;
        if (AppRoutes.ORDER_SETUP_SAMPLE_ENTRY.valueOf().indexOf(urlWithoutQueryParams) > -1) return true;
        if (AppRoutes.ORDER_SETUP_FINALIZE_ORDER.valueOf().indexOf(urlWithoutQueryParams) > -1) return true;

        return false;
    }
}
